import React from 'react';
import { useTranslation } from 'react-i18next';
import type User from '../../models/User';
import { Tooltip } from '../Tooltip';
import { UserAvatar } from '../UserAvatar';
import { Icon } from '../Icon';

export type MessageJumpItem = {
    id: string;
    author?: User;
    label: string;
    dateLabel: string;
    isEmail?: boolean;
    isSelf?: boolean;
};

type MessageJumpRailProps = {
    messages: MessageJumpItem[];
    activeId?: string | null;
    onJump: (id: string) => void;
};

function MessageJumpRail(props: MessageJumpRailProps) {
    const { messages, activeId, onJump } = props;
    const { t } = useTranslation();

    return (
        <nav
            data-testid="message-jump-rail"
            aria-label={t('changes.message_jump_rail') || ''}
            className="pointer-events-auto flex h-full min-h-0 flex-col items-center overflow-hidden py-0.5"
        >
            <ol className="relative m-0 flex h-full min-h-0 w-full list-none flex-col items-center justify-between p-0">
                <li
                    data-testid="message-jump-rail-line"
                    aria-hidden
                    className="pointer-events-none absolute left-1/2 top-3 bottom-3 z-0 w-0.5 -translate-x-1/2 rounded-full bg-gray-300"
                />
                {messages.map((message) => {
                    const selected = activeId === message.id;
                    const tooltip = message.isSelf
                        ? (t('changes.message_jump_to_self', { date: message.dateLabel }) || '')
                        : (t('changes.message_jump_to', {
                            author: message.label,
                            date: message.dateLabel,
                        }) || `${message.label} — ${message.dateLabel}`);
                    const avatar = message.author?._id ? (
                        <UserAvatar
                            user={message.author}
                            size={7}
                            hideInitials
                            noTooltip
                            className={`!w-7 !h-7 bg-white dark:bg-gray-800 ${selected ? 'ring-2 ring-blue-500' : 'ring-2 ring-white'}`}
                        />
                    ) : (
                        <span
                            className={`block h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 ${selected ? 'ring-2 ring-blue-500' : 'ring-2 ring-white'}`}
                        />
                    );

                    return (
                        <li key={message.id} className="relative z-[1] shrink-0">
                            <Tooltip content={tooltip} placement="left">
                                <button
                                    type="button"
                                    aria-label={tooltip}
                                    aria-current={selected ? 'true' : undefined}
                                    className="relative rounded-full border-0 bg-transparent p-0 leading-none cursor-pointer"
                                    onClick={() => onJump(message.id)}
                                >
                                    {avatar}
                                    {message.isEmail && (
                                        <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 ring-1 ring-gray-200">
                                            <Icon type="email" size={2} />
                                        </span>
                                    )}
                                </button>
                            </Tooltip>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

export default MessageJumpRail;
